import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { locations, propertyTypes } from '../propertyConfig';
import { FilterIcon } from './IconSet';

// Priority order the fields fold in, lowest priority (folds first) last -
// matches their existing left-to-right order in the desktop toolbar (state
// and area come first, listing/property type second), not an arbitrary
// choice.
export function buildQuickFilterFields({
  filterLocation,
  filterArea,
  filterType,
  filterPropertyType,
  setFilterLocation,
  setFilterArea,
  setFilterType,
  setFilterPropertyType,
  browseAreas,
}) {
  return [
    {
      key: 'state',
      label: 'State',
      render: (extra) => (
        <select
          className="ui-select"
          value={filterLocation}
          onChange={(event) => {
            setFilterLocation(event.target.value);
            setFilterArea('');
          }}
          {...extra}
        >
          <option value="">All States</option>
          {locations.map((stateName) => (
            <option key={stateName} value={stateName}>
              {stateName}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'area',
      label: 'Area',
      render: (extra) => (
        <select
          className="ui-select"
          value={filterArea}
          onChange={(event) => setFilterArea(event.target.value)}
          disabled={!filterLocation}
          {...extra}
        >
          <option value="">All Areas</option>
          {filterArea && !browseAreas.includes(filterArea) && (
            <option value={filterArea}>{filterArea}</option>
          )}
          {browseAreas.map((stateArea) => (
            <option key={stateArea} value={stateArea}>
              {stateArea}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'listingType',
      label: 'Listing type',
      render: (extra) => (
        <select
          className="ui-select"
          value={filterType}
          onChange={(event) => setFilterType(event.target.value)}
          {...extra}
        >
          <option value="">All Listing Types</option>
          <option value="Buy">For Sale</option>
          <option value="Short-let">Short-let</option>
          <option value="Rent">Rent</option>
        </select>
      ),
    },
    {
      key: 'propertyType',
      label: 'Property type',
      render: (extra) => (
        <select
          className="ui-select"
          value={filterPropertyType}
          onChange={(event) => setFilterPropertyType(event.target.value)}
          {...extra}
        >
          <option value="">Any Type</option>
          {propertyTypes
            .filter(([value]) => value !== 'Other')
            .map(([value, optLabel]) => (
              <option key={value} value={optLabel}>
                {optLabel}
              </option>
            ))}
        </select>
      ),
    },
  ];
}

/**
 * Mobile-only quick filter row. State/Area/Listing type/Property type are
 * active filters, not navigation, so they never scroll out of view (unlike
 * the site's chip-row navigation strips) - whichever ones don't fit at the
 * current width fold into the "More filters" drawer instead, in priority
 * order, and stay reachable there. See mobile-nav-proposal design notes.
 *
 * Folded fields are never unmounted: they stay rendered (clipped by the
 * row's overflow:hidden) so their natural width is always measurable if the
 * row gets wider again - but they're pulled out of the tab order and hidden
 * from assistive tech, since the same control is fully interactive inside
 * the drawer.
 */
function BrowseQuickFilters({ fields, measureKey, activeFilterCount, onOpenFilters, onFoldedChange }) {
  const rowRef = useRef(null);
  const itemRefs = useRef([]);
  const [visibleCount, setVisibleCount] = useState(fields.length);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) {
      return undefined;
    }

    const measure = () => {
      const containerWidth = row.clientWidth;
      // This row is display: none outside the mobile breakpoint (see
      // core.css), which reports a 0 clientWidth - without this guard, the
      // loop below would treat that as "nothing fits" and fold every field
      // but the first, which then leaked into FilterDrawer's "folded from
      // the toolbar" section on desktop too, duplicating fields the
      // always-visible desktop toolbar already shows. A container that
      // genuinely isn't laid out yet means "don't fold anything", not
      // "fold everything".
      if (containerWidth <= 0) {
        setVisibleCount(fields.length);
        return;
      }
      const gap = 6;
      let total = 0;
      let count = 0;
      for (let i = 0; i < fields.length; i += 1) {
        const el = itemRefs.current[i];
        if (!el) {
          break;
        }
        const width = el.offsetWidth + (i > 0 ? gap : 0);
        if (total + width > containerWidth && count > 0) {
          break;
        }
        total += width;
        count += 1;
      }
      setVisibleCount(count);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // Re-measure whenever a selected value changes (label width can change),
    // not just on resize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureKey, fields.length]);

  useEffect(() => {
    onFoldedChange(fields.slice(visibleCount).map((f) => f.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCount, fields.length]);

  return (
    <div className="browse-quick-filters">
      <div className="browse-quick-filters__row" ref={rowRef}>
        {fields.map((field, index) => {
          const folded = index >= visibleCount;
          return (
            <span
              key={field.key}
              className="browse-quick-filters__item"
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              aria-hidden={folded || undefined}
            >
              {field.render(folded ? { tabIndex: -1 } : {})}
            </span>
          );
        })}
      </div>
      <button
        type="button"
        className={`browse-quick-filters__more${activeFilterCount > 0 ? ' browse-quick-filters__more--active' : ''}`}
        onClick={onOpenFilters}
        aria-label="More filters"
      >
        <FilterIcon size={15} />
        {activeFilterCount > 0 && <span className="browse-quick-filters__badge">{activeFilterCount}</span>}
      </button>
    </div>
  );
}

BrowseQuickFilters.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string.isRequired, render: PropTypes.func.isRequired }),
  ).isRequired,
  measureKey: PropTypes.string.isRequired,
  activeFilterCount: PropTypes.number,
  onOpenFilters: PropTypes.func.isRequired,
  onFoldedChange: PropTypes.func.isRequired,
};

export default BrowseQuickFilters;
